import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import VideoTestimonial from '@/components/home/VideoTestimonial';
import Video3DWrapper from '@/components/home/Video3DWrapper';
import { Calendar, Check, ChevronUp, Code, ExternalLink, MessageCircle, PhoneCall, Shield, Sparkles, TrendingUp, X, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import emailjs from 'emailjs-com';

const AffordableWebsiteDesign = () => {
  const { toast } = useToast();
  const formRef = React.useRef<HTMLFormElement>(null);
  const [replyTo, setReplyTo] = React.useState('');
  const [showReturnToForm, setShowReturnToForm] = React.useState(false);
  const whatsappUrl = 'https://wa.me/12026998621';
  const EMAILJS_USER_ID = 'IiRfsZbuWCZHwgBlt';

  React.useEffect(() => {
    const handleScroll = () => {
      setShowReturnToForm(window.scrollY > 520);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleScrollToForm = () => {
    const formSection = document.getElementById('application-form');
    if (!formSection) return;

    formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formRef.current) return;

    const form = formRef.current;

    try {
      await emailjs.sendForm(
        'service_db1w009',
        'template_0dqc2ot',
        form,
        EMAILJS_USER_ID
      );

      toast({
        title: 'Message sent!',
        description: "Thanks for applying. We'll contact you shortly.",
      });
      form.reset();
      setReplyTo('');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'There was a problem sending your message. Please try again later.',
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Affordable Website Design Under $99 | USA & Canada | Omnicraft</title>
        <meta name="description" content="Get a custom-coded website under $99 with 2 pages included for your small business in USA or Canada. Professional, fast, and secure - not WordPress or Wix." />
        <meta name="keywords" content="affordable website design, cheap website, $99 website, small business website, custom coded website, affordable web design USA, affordable web design Canada, professional website, not WordPress, not Wix, custom website development" />
        <meta property="og:title" content="Affordable Website Design Under $99 | USA & Canada | Omnicraft" />
        <meta property="og:description" content="Get a custom-coded, professional 2-page website under $99 for your small business. No WordPress, no Wix - 100% custom coded. After 2 pages, $20/month hosting applies." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.omnicraftservices.com/affordable-website-design" />
        <meta property="og:video" content="https://www.omnicraftservices.com/hero-demo.mp4" />
        <meta name="twitter:card" content="player" />
        <meta name="twitter:title" content="Affordable Website Design Under $99 | 2 Pages Included" />
        <meta name="twitter:description" content="Get a custom-coded website under $99 for your small business. Professional, fast, secure." />
        <link rel="canonical" href="https://www.omnicraftservices.com/affordable-website-design" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Offer",
          "name": "Affordable Custom-Coded Website Under $99 for Small Businesses",
          "description": "Professional 2-page custom-coded website for small businesses in USA and Canada, available for under $99. After 2 pages, $20/month hosting applies.",
          "price": "99",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock",
          "url": "https://www.omnicraftservices.com/affordable-website-design",
          "areaServed": ["US", "CA"],
          "additionalProperty": {
            "@type": "PropertyValue",
            "name": "Hosting Fee",
            "value": "After 2 pages, $20/month"
          }
        })}</script>
      </Helmet>

      <div className="relative min-h-screen overflow-hidden bg-[#fbfaf5] text-slate-900 dark:bg-slate-950 dark:text-white">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-28 left-[-10%] h-[34rem] w-[34rem] rounded-full bg-emerald-300/45 blur-3xl dark:bg-emerald-400/12" />
          <div className="absolute top-16 right-[-10%] h-[36rem] w-[36rem] rounded-full bg-amber-200/40 blur-3xl dark:bg-amber-300/12" />
          <div className="absolute bottom-[-10rem] left-[14%] h-[30rem] w-[30rem] rounded-full bg-lime-200/42 blur-3xl dark:bg-lime-400/12" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.98),_rgba(255,255,255,0.78)_22%,_rgba(251,250,245,0.5)_55%,_rgba(243,249,239,0.32)_75%,_rgba(243,249,239,0)_100%)] dark:bg-[radial-gradient(circle_at_top,_rgba(15,23,42,0.7),_rgba(15,23,42,0.86)_45%,_rgba(2,6,23,1)_100%)]" />
        </div>

        <div className="relative z-10">
          <section className="px-4 pt-28 pb-8 sm:pt-32 sm:pb-10">
            <div className="mx-auto max-w-6xl text-center">

              <h1 className="mx-auto max-w-4xl text-4xl font-semibold leading-tight text-slate-950 md:text-6xl dark:text-white">
                Get a Website Under $99 
              </h1>

              <div className="mt-10 flex justify-center sm:hidden">
                <Button
                  onClick={handleScrollToForm}
                  size="lg"
                  className="h-14 rounded-full bg-slate-950 px-8 text-base text-white shadow-[0_20px_60px_rgba(15,23,42,0.28)] transition-transform duration-300 hover:scale-[1.02] hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
                >
                  <Calendar className="mr-2 h-5 w-5" />
                  Apply Now
                </Button>
              </div>

              <p className="mt-6 text-base text-emerald-700 dark:text-emerald-400">
                Prefer to message? <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="font-semibold text-emerald-700 underline decoration-2 underline-offset-4 dark:text-emerald-400">Message us on WhatsApp</a>
              </p>
            </div>
          </section>

          <section id="application-form" className="px-4 pb-20 pt-4 sm:pb-28">
            <div className="mx-auto max-w-4xl">
              <div className="rounded-[2.25rem] border border-[#e7e0cf] bg-[#f8f5ec]/96 p-4 shadow-[0_30px_90px_rgba(15,23,42,0.12)] backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/65 sm:p-6 md:p-8">
                <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Name
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        placeholder="Your name"
                        required
                        className="h-14 rounded-2xl border-[#ddd9cc] bg-[#f5f4f2] px-4 text-base text-slate-700 shadow-[0_2px_8px_rgba(15,23,42,0.05)] placeholder:text-slate-500 focus-visible:ring-emerald-500 dark:border-slate-700 dark:bg-slate-950/60 dark:text-slate-100 dark:placeholder:text-slate-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Email
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Your email"
                        required
                        onChange={(event) => setReplyTo(event.target.value)}
                        className="h-14 rounded-2xl border-[#ddd9cc] bg-[#f5f4f2] px-4 text-base text-slate-700 shadow-[0_2px_8px_rgba(15,23,42,0.05)] placeholder:text-slate-500 focus-visible:ring-emerald-500 dark:border-slate-700 dark:bg-slate-950/60 dark:text-slate-100 dark:placeholder:text-slate-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="Your phone number"
                        autoComplete="tel"
                        className="h-14 rounded-2xl border-[#ddd9cc] bg-[#f5f4f2] px-4 text-base text-slate-700 shadow-[0_2px_8px_rgba(15,23,42,0.05)] placeholder:text-slate-500 focus-visible:ring-emerald-500 dark:border-slate-700 dark:bg-slate-950/60 dark:text-slate-100 dark:placeholder:text-slate-400"
                      />
                    </div>
                  </div>

                  <input type="hidden" name="reply_to" value={replyTo} />

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Project Details
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      rows={3}
                      placeholder="Tell us about your business, what you offer, and your preferred launch timeline."
                      required
                      className="min-h-20 rounded-2xl border-[#ddd9cc] bg-[#f5f4f2] px-4 py-4 text-base text-slate-700 shadow-[0_2px_8px_rgba(15,23,42,0.05)] placeholder:text-slate-500 focus-visible:ring-emerald-500 dark:border-slate-700 dark:bg-slate-950/60 dark:text-slate-100 dark:placeholder:text-slate-400"
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="h-14 w-full rounded-2xl bg-black text-base font-medium text-white shadow-[0_16px_34px_rgba(0,0,0,0.45)] transition-colors duration-300 hover:bg-[#121212]"
                  >
                    Apply for My $99 Website
                  </Button>

                  <div className="flex flex-col items-center gap-4 pt-2 sm:flex-row sm:justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-500 dark:text-slate-400">Follow us:</span>
                      <a
                        href="https://www.instagram.com/omnicraft_services/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Instagram"
                        className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 text-white shadow-sm transition-transform duration-200 hover:scale-110"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                      </a>
                      <a
                        href="https://www.facebook.com/search/top?q=omnicraft%20services"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Facebook"
                        className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#1877F2] text-white shadow-sm transition-transform duration-200 hover:scale-110"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                      </a>
                    </div>

                    <Link
                      to="/portfolio"
                      className="inline-flex items-center gap-2 rounded-xl border border-[#ddd9cc] bg-white/80 px-5 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <ExternalLink className="h-4 w-4" />
                      View Our Website Projects
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </section>

          <section className="px-4 py-14 sm:py-20">
            <div className="mx-auto max-w-7xl">
              <div className="mb-14 text-center">
                <p className="mb-4 text-xs font-semibold uppercase tracking-[0.34em] text-emerald-700 dark:text-emerald-400">
                  Your Tailored Solution
                </p>
                <h2 className="mx-auto max-w-4xl text-3xl font-semibold leading-tight text-slate-950 md:text-5xl dark:text-white">
                  Here&apos;s what to expect
                </h2>
              </div>

              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                {[
                  {
                    step: '1',
                    title: 'Professional Design',
                    description:
                      'Modern, mobile-responsive design that makes your business stand out and converts visitors into customers.',
                    icon: Sparkles,
                    accent: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-400',
                  },
                  {
                    step: '2',
                    title: 'Lightning Fast',
                    description:
                      'Custom-coded websites load 3x faster than WordPress, giving you better SEO rankings and user experience.',
                    icon: Zap,
                    accent: 'bg-sky-100 text-sky-700 dark:bg-sky-400/15 dark:text-sky-400',
                  },
                  {
                    step: '3',
                    title: 'Secure & Reliable',
                    description:
                      'No plugins or themes means no security vulnerabilities. Your website stays safe and performs flawlessly.',
                    icon: Shield,
                    accent: 'bg-slate-100 text-slate-700 dark:bg-slate-700/60 dark:text-slate-300',
                  },
                  {
                    step: '4',
                    title: 'Delivered in 7 Days',
                    description:
                      'Get your online presence up and running fast. Home page, about/contact page, and mobile-optimized—all within one week.',
                    icon: TrendingUp,
                    accent: 'bg-amber-100 text-amber-700 dark:bg-amber-400/15 dark:text-amber-400',
                  },
                ].map(({ step, title, description, icon: Icon, accent }) => (
                  <Card
                    key={step}
                    className="rounded-[1.75rem] border border-[#e7e0cf] bg-white/90 shadow-[0_16px_40px_rgba(15,23,42,0.08)] transition-transform duration-300 hover:-translate-y-1 dark:border-white/10 dark:bg-slate-900/65"
                  >
                    <CardContent className="flex h-full flex-col items-center p-8 text-center">
                      <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-lg bg-emerald-600 text-lg font-semibold text-white shadow-[0_10px_25px_rgba(16,185,129,0.3)]">
                        {step}
                      </div>
                      <h3 className="mb-4 text-2xl font-semibold text-slate-950 dark:text-white">{title}</h3>
                      <p className="text-lg leading-8 text-slate-600 dark:text-slate-400">{description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="mt-8 overflow-hidden rounded-[2rem] border border-[#e7e0cf] bg-gradient-to-br from-[#fbfaf5] via-[#f5f1e3] to-[#eef5e9] shadow-[0_24px_80px_rgba(15,23,42,0.1)] dark:border-white/10 dark:from-slate-900/80 dark:via-slate-900/75 dark:to-slate-800/75">
                <div className="p-8 md:p-10">
                  <div className="mx-auto max-w-3xl text-center mb-12">
                    <h3 className="text-3xl font-semibold leading-tight text-slate-950 md:text-5xl dark:text-white">
                      What&apos;s Included ?
                    </h3>
                  </div>

                  <div className="mt-12 grid gap-6 lg:grid-cols-2">
                    <Card className="rounded-[1.75rem] border border-[#dfe8d8] bg-white/80 shadow-[0_16px_40px_rgba(15,23,42,0.08)] dark:border-emerald-900/60 dark:bg-emerald-950/25">
                      <CardContent className="p-8">
                        <div className="mb-8">
                          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.34em] text-emerald-700 dark:text-emerald-400">
                            What's Included
                          </p>
                          <h4 className="text-2xl font-semibold text-slate-950 dark:text-white">2-Page Professional Website</h4>
                        </div>

                        <ul className="space-y-3 text-slate-700 dark:text-slate-300">
                          <li className="flex items-start gap-3">
                            <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                            <span><strong>Home Page:</strong> Showcase your business, services, and unique value proposition</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                            <span><strong>About/Contact Page:</strong> Tell your story and make it easy for customers to reach you</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                            <span>Mobile-responsive design that looks perfect on all devices</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                            <span>Contact form and social media integration</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                            <span>Basic SEO optimization to help customers find you</span>
                          </li>
                        </ul>
                      </CardContent>
                    </Card>

                    <Card className="rounded-[1.75rem] border border-[#e7e0cf] bg-white/80 shadow-[0_16px_40px_rgba(15,23,42,0.08)] dark:border-slate-700/70 dark:bg-slate-900/70">
                      <CardContent className="p-8">
                        <div className="mb-8">
                          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.34em] text-slate-700 dark:text-slate-400">
                            Why Choose Us
                          </p>
                          <h4 className="text-2xl font-semibold text-slate-950 dark:text-white">Custom-Coded Beats WordPress & Wix</h4>
                        </div>

                        <div className="grid gap-4">
                          <div className="rounded-[1.5rem] border border-emerald-200 bg-emerald-50/80 p-5 dark:border-emerald-900/60 dark:bg-emerald-950/20">
                            <div className="mb-4 text-sm font-semibold uppercase tracking-[0.28em] text-emerald-700 dark:text-emerald-400">
                              Our Approach
                            </div>
                            <ul className="space-y-3">
                              <li className="flex items-start gap-3">
                                <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                                <span className="text-sm text-slate-700 dark:text-slate-300"><strong>Lightning Fast:</strong> Loads in under 1 second, better SEO rankings</span>
                              </li>
                              <li className="flex items-start gap-3">
                                <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                                <span className="text-sm text-slate-700 dark:text-slate-300"><strong>100% Secure:</strong> No plugins or themes to hack</span>
                              </li>
                              <li className="flex items-start gap-3">
                                <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                                <span className="text-sm text-slate-700 dark:text-slate-300"><strong>Fully Custom:</strong> Built specifically for your business needs</span>
                              </li>
                              <li className="flex items-start gap-3">
                                <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                                <span className="text-sm text-slate-700 dark:text-slate-300"><strong>No Bloat:</strong> Clean code, no unnecessary features</span>
                              </li>
                              <li className="flex items-start gap-3">
                                <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                                <span className="text-sm text-slate-700 dark:text-slate-300"><strong>Better Performance:</strong> 3x faster than WordPress</span>
                              </li>
                              <li className="flex items-start gap-3">
                                <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                                <span className="text-sm text-slate-700 dark:text-slate-300"><strong>Future-Proof:</strong> Modern technology stack</span>
                              </li>
                            </ul>
                          </div>

                          <div className="rounded-[1.5rem] border border-rose-200 bg-rose-50/80 p-5 dark:border-rose-900/40 dark:bg-rose-950/20">
                            <div className="mb-4 text-sm font-semibold uppercase tracking-[0.28em] text-rose-700 dark:text-rose-400">
                              Why Not WordPress / Wix / Framer
                            </div>
                            <ul className="space-y-3">
                              <li className="flex items-start gap-3">
                                <X className="mt-0.5 h-5 w-5 flex-shrink-0 text-rose-600" />
                                <span className="text-sm text-slate-700 dark:text-slate-300"><strong>Slow Loading:</strong> Heavy plugins make sites sluggish</span>
                              </li>
                              <li className="flex items-start gap-3">
                                <X className="mt-0.5 h-5 w-5 flex-shrink-0 text-rose-600" />
                                <span className="text-sm text-slate-700 dark:text-slate-300"><strong>Security Risks:</strong> Vulnerable to hacks and attacks</span>
                              </li>
                              <li className="flex items-start gap-3">
                                <X className="mt-0.5 h-5 w-5 flex-shrink-0 text-rose-600" />
                                <span className="text-sm text-slate-700 dark:text-slate-300"><strong>Template-Based:</strong> Looks like thousands of other sites</span>
                              </li>
                              <li className="flex items-start gap-3">
                                <X className="mt-0.5 h-5 w-5 flex-shrink-0 text-rose-600" />
                                <span className="text-sm text-slate-700 dark:text-slate-300"><strong>Bloated Code:</strong> Tons of unused features slow you down</span>
                              </li>
                              <li className="flex items-start gap-3">
                                <X className="mt-0.5 h-5 w-5 flex-shrink-0 text-rose-600" />
                                <span className="text-sm text-slate-700 dark:text-slate-300"><strong>Constant Updates:</strong> Requires ongoing maintenance</span>
                              </li>
                              <li className="flex items-start gap-3">
                                <X className="mt-0.5 h-5 w-5 flex-shrink-0 text-rose-600" />
                                <span className="text-sm text-slate-700 dark:text-slate-300"><strong>Poor SEO:</strong> Slower sites rank lower on Google</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="px-4 pb-4 pt-6 sm:pb-8 sm:pt-10">
            <div className="mx-auto max-w-7xl">
              <Video3DWrapper>
                <VideoTestimonial />
              </Video3DWrapper>
            </div>
          </section>

          <section className="px-4 py-14 sm:py-20">
            <div className="mx-auto max-w-7xl">
              <div className="mb-12 text-center">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#1877F2]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.28em] text-[#1877F2] dark:bg-[#1877F2]/15">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook Reviews
                </div>
                <h2 className="mx-auto max-w-4xl text-3xl font-semibold leading-tight text-slate-950 md:text-5xl dark:text-white">
                  What Our Clients Say
                </h2>
                <p className="mt-4 text-base text-slate-600 dark:text-slate-400">
                  Real recommendations from real businesses on Facebook.
                </p>
              </div>

              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {[
                  {
                    name: 'Pietro Baker',
                    initials: 'PB',
                    avatarColor: 'bg-gradient-to-br from-emerald-500 to-teal-600',
                    review:
                      'OmniCraft Services built my website and provided excellent advice on how to make it more aesthetically pleasing and customer-focused. Their team is knowledgeable, professional, and incredibly informative. They took the time to ensure my site not only looked great but was designed to attract and engage more customers. I highly recommend OmniCraft Services to anyone looking for quality web design and a team that truly knows their craft.',
                    reply: 'Thankyou sir !!',
                  },
                  {
                    name: 'Michael Nwosu',
                    initials: 'MN',
                    avatarColor: 'bg-gradient-to-br from-amber-500 to-orange-600',
                    review:
                      'This company has been amazing to work with and they have taken the time to educate me on everything they do. They are responsive, friendly and willing to take their time to provide a quality service. Our SEO and digital footprint has improved tremendously since they onboarded us. I look forward to continuing to work with them and make strides to improve our digital side of our business.',
                    reply: 'Thankyou mike for your kind words.',
                  },
                  {
                    name: 'Kogit Labs',
                    initials: 'KL',
                    avatarColor: 'bg-gradient-to-br from-sky-500 to-indigo-600',
                    review:
                      'Omnicraft Services is great for making custom websites. We would recommend it to anyone who wants to get a site online without any hassle, as-well as service that goes above and beyond!',
                    reply: 'Thankyou for your kind words! 🙌',
                  },
                ].map(({ name, initials, avatarColor, review, reply }) => (
                  <Card
                    key={name}
                    className="flex h-full flex-col rounded-[1.75rem] border border-[#e7e0cf] bg-white/90 shadow-[0_16px_40px_rgba(15,23,42,0.08)] transition-transform duration-300 hover:-translate-y-1 dark:border-white/10 dark:bg-slate-900/65"
                  >
                    <CardContent className="flex h-full flex-col p-6 sm:p-7">
                      <div className="mb-4 flex items-center gap-3">
                        <div className={`flex h-12 w-12 items-center justify-center rounded-full text-base font-semibold text-white shadow-sm ${avatarColor}`}>
                          {initials}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm font-semibold text-slate-950 dark:text-white">{name}</p>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#1877F2" className="h-4 w-4">
                              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                            </svg>
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">recommends OmniCraft Services</p>
                        </div>
                      </div>

                      <p className="mb-5 flex-1 text-sm leading-7 text-slate-700 dark:text-slate-300">
                        &ldquo;{review}&rdquo;
                      </p>

                      <div className="rounded-2xl border border-[#e7e0cf] bg-[#f8f5ec]/70 p-4 dark:border-slate-700/70 dark:bg-slate-950/40">
                        <div className="mb-1 flex items-center gap-2">
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 text-[10px] font-bold text-white">
                            OC
                          </div>
                          <p className="text-xs font-semibold text-slate-950 dark:text-white">OmniCraft Services</p>
                        </div>
                        <p className="pl-9 text-sm text-slate-600 dark:text-slate-400">{reply}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="mt-10 flex justify-center">
                <a
                  href="https://www.facebook.com/search/top?q=omnicraft%20services"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-[#1877F2] px-6 py-3 text-sm font-medium text-white shadow-[0_14px_30px_rgba(24,119,242,0.35)] transition-transform duration-300 hover:scale-[1.02] hover:bg-[#166fe0]"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  See All Reviews on Facebook
                </a>
              </div>
            </div>
          </section>

          <section className="px-4 py-10">
            <div className="mx-auto max-w-4xl text-center">
              <p className="mb-4 text-sm text-slate-500 dark:text-slate-500">
                Full terms are available on the separate terms page.
              </p>
              <a
                href="/terms"
                className="inline-flex items-center justify-center rounded-full border border-[#d9d1bd] bg-white/80 px-6 py-3 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                View Full Terms
              </a>
            </div>
          </section>

          <section className="px-4 pb-20 pt-10 sm:pb-28">
            <div className="mx-auto max-w-4xl rounded-[2.25rem] bg-slate-950 px-6 py-14 text-center text-white shadow-[0_24px_80px_rgba(15,23,42,0.24)] dark:bg-white dark:text-slate-950">
              <h2 className="text-3xl font-semibold md:text-5xl">
                Ready to Take Your Business Online?
              </h2>
              <p className="mx-auto mt-6 max-w-2xl text-lg text-white/85 dark:text-slate-700 md:text-xl">
                Don't miss this chance to get a professional, custom-coded website for under $99 — 2 pages included!
              </p>

              <div className="mt-10 flex justify-center">
                <Button
                  onClick={handleScrollToForm}
                  size="lg"
                  className="h-14 rounded-full bg-white px-8 text-base text-slate-950 shadow-lg transition-transform duration-300 hover:scale-[1.02] hover:bg-slate-100 dark:bg-slate-950 dark:text-white dark:hover:bg-slate-800"
                >
                  <ChevronUp className="mr-2 h-5 w-5" />
                  Apply for Your $99 Website
                </Button>
              </div>
            </div>
          </section>

          <div
            className={`pointer-events-none fixed bottom-5 right-4 z-50 transition-all duration-300 sm:bottom-8 sm:right-8 ${
              showReturnToForm ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
            }`}
          >
            <Button
              onClick={handleScrollToForm}
              size="sm"
              aria-label="Return to application form"
              className="pointer-events-auto h-11 rounded-full bg-slate-950 px-5 text-sm font-medium text-white shadow-[0_14px_30px_rgba(15,23,42,0.34)] hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
            >
              <ChevronUp className="mr-2 h-4 w-4" />
              Return to Form
            </Button>
          </div>

          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Message us on WhatsApp"
            className="fixed bottom-20 right-4 z-50 inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-600 text-white shadow-[0_14px_30px_rgba(5,150,105,0.35)] transition-transform duration-300 hover:scale-110 hover:bg-emerald-500 sm:bottom-24 sm:right-8"
          >
            <MessageCircle className="h-5 w-5" />
          </a>
        </div>
      </div>
    </>
  );
};

export default AffordableWebsiteDesign;
